
import axios, { AxiosResponse } from 'axios';
import Resource from './Resource';
import ShootContract from '../contracts/Shoot';
import User from "./User";
import Session from "../support/Session";
import Item from "./Item";
import Photo from "./Photo";

/**
 * Shoot class
 * @class
 **/
class Shoot extends Resource implements ShootContract
{
    /**
     * Resource identifier
     * @type {String}
     **/
    static handle = 'shoot_id';

    /**
     * Default attributes
     * @type {Object}
     **/
    static attributes: ShootContract = {
        shoot_id: undefined,
        uuid: undefined,
        title: undefined,
        location: undefined,
        taken_at: undefined,
        published_at: undefined,
    };

    /**
     * Resource attributes
     * @type {ShootContract}
     **/
    protected attributes: ShootContract;

    /**
     * Resource relations
     * @type {Object}
     **/
    static relations = {
        photos: {
            related: Photo,
            default: [],
        },
    };

    /**
     * Current attributes
     **/
    shoot_id: number;
    uuid: string;
    title: string;
    location: string;
    taken_at: string;
    published_at: string;

    photos: Photo[];


    /**
     * Get all shoots
     * @return {Promise<Shoot[]>}
     */
    static all(): Promise<Shoot[]>
    {
        return axios.get( '/api/shoots' )
            .then( response => response.data.map( attributes => new Shoot( attributes, true ) ) );
    }


    /**
     * Get shoot by id
     * @param {Number} uuid
     */
    static fetch( uuid: string ): Promise<Shoot>
    {
        return axios.get( `/api/shoot/${ uuid }` ).then( response => {
            const shoot = new Shoot( response.data, true );
            Session.put( shoot );
            return shoot;
        });
    }


    /**
     * Apply attributes
     * @param {Object} attributes
     * @return {Promise<>}
     */
    private apply( attributes: any )
    {
        const action = this.exists ? 'patch' : 'put',
            endpoint = this.exists ? `/api/shoot/${ this.shoot_id }` : '/api/shoot';

        this.saving = true;
        return axios[action]( endpoint, attributes ).then( response => {
            this.sync( response.data );
            this.saving = false;

            return this as Shoot;
        });
    }


    /**
     * Delete shoot
     * @return Promise<Boolean>
     */
    delete()
    {
        return axios.delete( `/api/shoot/${ this.shoot_id }` );
    }


    /**
     * Publish shoot
     * @return {Promise<>}
     */
    publish()
    {
        return this.apply(
            {
                published_at: ( new Date ).toISOString().slice( 0, 19 ).replace( 'T', ' ' ),
            }
        );
    }


    /**
     * Save shoot
     * @return {Promise<>}
     */
    save()
    {
        return this.apply( this.changes );
    }


    /**
     * Associate user with shoot
     * @param {User} user
     * @return {Promise<AxiosResponse>}
     */
    associate( user: User ): Promise<AxiosResponse>
    {
        return axios.put( `/api/shoot/${ this.shoot_id }/user/${ user.user_id }` );
    }


    /**
     * Disassociate user with shoot
     * @param {User} user
     * @return {Promise<AxiosResponse>}
     */
    disassociate( user: User ): Promise<AxiosResponse>
    {
        return axios.delete( `/api/shoot/${ this.shoot_id }/user/${ user.user_id }` );
    }
}

Resource.register( Shoot );

export default Shoot;
